"use client"

import { useState } from "react"
import { ChevronLeftIcon, ChevronRightIcon, DoubleArrowLeftIcon, DoubleArrowRightIcon } from "@radix-ui/react-icons"
import { useTranslations } from "next-intl"

import { cn } from "@/lib/utils"

import { Pagination, PaginationContent, PaginationItem, PaginationLink } from "./pagination"

// --- Logik mit der korrekten "Unpacking"-Funktionalität ---

interface PaginationInfoItem {
  start: number
  end: number
}

/**
 * Erzeugt die Paginierungsinformationen. Die Gruppe, die die aktuelle Seite
 * enthält, wird in einzelne Seiten "aufgeklappt".
 * @param groupSize - Anzahl der Seiten pro Gruppe.
 * @param currentPage - Die aktuell ausgewählte Seite.
 * @param totalPages - Die Gesamtanzahl der Seiten.
 * @param visibleGroups - Die maximale Anzahl an sichtbaren Gruppen/Seiten-Links.
 * @returns Ein Array von Paginierungselementen.
 */
const getPaginationInfo = (
  groupSize: number,
  currentPage: number,
  totalPages: number,
  visibleGroups: number
): PaginationInfoItem[] => {
  if (totalPages <= 1) return []

  const totalGroups = Math.ceil(totalPages / groupSize)
  const currentGroup = Math.ceil(currentPage / groupSize)

  let startGroup = Math.max(1, currentGroup - Math.floor(visibleGroups / 2))
  let endGroup = Math.min(totalGroups, startGroup + visibleGroups - 1)

  if (endGroup - startGroup + 1 < visibleGroups) {
    startGroup = Math.max(1, endGroup - visibleGroups + 1)
  }

  const paginationItems: PaginationInfoItem[] = []
  for (let i = startGroup; i <= endGroup; i++) {
    const groupStartPage = (i - 1) * groupSize + 1
    const groupEndPage = Math.min(i * groupSize, totalPages)

    // KORREKTUR: Prüfen, ob dies die aktive Gruppe ist.
    if (i === currentGroup) {
      // Wenn ja, klappe die Gruppe in einzelne Seiten auf.
      for (let pageNum = groupStartPage; pageNum <= groupEndPage; pageNum++) {
        paginationItems.push({ start: pageNum, end: pageNum })
      }
    } else {
      // Wenn nicht, füge die Gruppe als Ganzes hinzu.
      paginationItems.push({ start: groupStartPage, end: groupEndPage })
    }
  }

  return paginationItems
}

// --- Die React-Komponente ---

interface DataPaginationProps {
  initialPage: number
  totalPages: number
  onPageChange: (page: number) => void
  groupSize?: number
  visibleGroups?: number
}

export const DataPagination = ({
  initialPage,
  totalPages,
  onPageChange,
  groupSize = 3,
  visibleGroups = 5,
}: DataPaginationProps) => {
  const t = useTranslations("storefront")
  const [currentPage, setCurrentPage] = useState(initialPage)

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return
    setCurrentPage(page)
    onPageChange(page)
    window?.scrollTo({ top: 0, behavior: "smooth" })
  }

  const createPageClickHandler = (page: number) => (e: React.MouseEvent) => {
    e.preventDefault()
    handlePageChange(page)
  }

  if (totalPages <= 1) {
    return null
  }

  const paginationInfo = getPaginationInfo(groupSize, currentPage, totalPages, visibleGroups)

  return (
    <Pagination>
      <PaginationContent className="flex flex-row items-center gap-x-3 md:gap-x-3 lg:gap-x-4">
        {/* Navigationselemente bleiben unverändert */}
        <PaginationItem>
          <PaginationLink
            size={"paginationLink"}
            href="#"
            onClick={createPageClickHandler(1)}
            title={t("pagination.first")}
            className={cn("pt-[0.19rem]", {
              "pointer-events-none text-gray2": currentPage === 1,
            })}
            aria-disabled={currentPage === 1}
          >
            <DoubleArrowLeftIcon className="size-6" />
          </PaginationLink>
        </PaginationItem>
        <PaginationItem>
          <PaginationLink
            size={"paginationLink"}
            href="#"
            onClick={createPageClickHandler(currentPage - 1)}
            title={t("pagination.previous")}
            className={cn("pt-[0.19rem]", {
              "pointer-events-none text-gray2": currentPage === 1,
            })}
            aria-disabled={currentPage === 1}
          >
            <ChevronLeftIcon className="size-6" />
          </PaginationLink>
        </PaginationItem>

        {/* Das Rendering der Seitenzahlen funktioniert dank der neuen Logik nun korrekt */}
        {paginationInfo.map((piItem) => {
          // Für einzelne Seiten ist start === end, also ist die Bedingung
          // `currentPage === piItem.start` ausreichend.
          const isActive = currentPage === piItem.start && piItem.start === piItem.end
          return (
            <PaginationItem key={"page-item-" + piItem.start}>
              <PaginationLink
                size={"paginationLink"}
                href="#"
                onClick={createPageClickHandler(piItem.start)}
                title={
                  piItem.start === piItem.end
                    ? t("pagination.page", {
                        page: piItem.start,
                        total: totalPages,
                      })
                    : t("pagination.pages", {
                        page: `${piItem.start}-${piItem.end}`,
                        total: totalPages,
                      })
                }
                className={cn("flex size-6 items-center justify-center text-center font-normal md:size-8", {
                  "rounded-full bg-primary text-white": isActive && currentPage !== totalPages,
                  "rounded-full bg-gray-200 text-gray-800": isActive && currentPage === totalPages,
                  "pt-[0.19rem]": !isActive,
                })}
              >
                {piItem.start === piItem.end ? piItem.start : `${piItem.start}-${piItem.end}`}
              </PaginationLink>
            </PaginationItem>
          )
        })}

        {/* Navigationselemente bleiben unverändert */}
        <PaginationItem>
          <PaginationLink
            size={"paginationLink"}
            href="#"
            onClick={createPageClickHandler(currentPage + 1)}
            title={t("pagination.next")}
            className={cn("pt-[0.19rem]", {
              "pointer-events-none text-gray2": currentPage === totalPages,
            })}
            aria-disabled={currentPage === totalPages}
          >
            <ChevronRightIcon className="size-6" />
          </PaginationLink>
        </PaginationItem>
        <PaginationItem>
          <PaginationLink
            size={"paginationLink"}
            href="#"
            onClick={createPageClickHandler(totalPages)}
            title={t("pagination.last")}
            className={cn("pt-[0.19rem]", {
              "pointer-events-none text-gray2": currentPage === totalPages,
            })}
            aria-disabled={currentPage === totalPages}
          >
            <DoubleArrowRightIcon className="size-6" />
          </PaginationLink>
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  )
}
